import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { useToast } from '@/components/ui'
import { useConfig } from '@/contexts/configProvider'
import { projectService } from '@/services/http/projectService'

import type { QueryParams } from '@/utils/queryParams'

type Props = {
  query: QueryParams & { type: 'all' | 'responsible' | 'member' }
}

export const useMyProjects = ({ query }: Props) => {
  const { page = 1, limit = 2, sort, type, ...rest } = query
  const { toast } = useToast()
  const { config } = useConfig()

  // `=== true` de propósito: `enabled: undefined` vale como `true` no TanStack
  // Query v5, então a query dispararia enquanto a config do tenant carrega.
  const hasProjectModule = config?.hasProjectModule === true

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      'myProjects',
      {
        limit,
        ...(sort && {
          sort,
        }),
        type,
        ...rest,
      },
    ],
    queryFn: ({ pageParam = page }) =>
      projectService.project.getMyProjects({
        query: { page: pageParam, limit, sort, type, ...rest },
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.page < lastPage.pagination.pages) {
        return lastPage.pagination.page + 1
      }

      return undefined
    },
    initialPageParam: page,
    enabled: hasProjectModule,
  })

  useEffect(() => {
    if (isError)
      toast({
        title: 'Erro ao carregar os projetos',
        variant: 'destructive',
      })
  }, [isError, toast])

  const myProjects = data?.pages.flatMap((page) => page.projects) ?? []

  return {
    myProjects,
    isLoading,
    isLoadingNextPage: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetchMyProjects: refetch,
  }
}
